import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Timer } from 'lucide-react';

/**
 * 60-second drinks-break overlay shown mid-game (e.g. at 11 in a 21-point game).
 * Displays current score + a premium-priority ad card; falls back to the
 * Ultimate Badminton Guide CTA if no admin-configured ad is present.
 */
export default function IntervalAdCard({ state, onResume, adConfig }) {
  const [remaining, setRemaining] = useState(60);
  const [liveCount, setLiveCount] = useState(null);

  useEffect(() => {
    const id = setInterval(() => {
      setRemaining(r => {
        if (r <= 1) { clearInterval(id); return 0; }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // Fetch the live-match count once when the overlay mounts. The backend
  // returns a real count or a random 12-99 if real < 10 so the ticker always
  // shows healthy traction.
  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${base}/api/public/matches/live/count`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && typeof d.count === 'number') setLiveCount(d.count); })
      .catch(() => { /* silent — ticker is non-critical */ });
  }, []);

  const game = state.games?.[state.currentGameIdx] || { scoreA: 0, scoreB: 0 };
  const sideAName = state.sideA || 'Side A';
  const sideBName = state.sideB || 'Side B';
  const adImg = (adConfig?.interval_image_url || '').trim();
  const adClick = (adConfig?.click_through_url || '').trim()
    || 'https://bwfbadminton.com/news-single/2023/09/06/laws-of-badminton-explained';
  const adText = (adConfig?.interval_text || '').trim();
  const hasValidImg = /^https?:\/\//.test(adImg);

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-md flex items-center justify-center p-4 safe-top safe-bottom" data-testid="interval-overlay">
      <Card className="w-full max-w-md border-2 border-primary/40">
        <CardContent className="p-6 space-y-5">
          <div className="text-center">
            <Badge className="bg-primary/15 text-primary border-primary/30 mb-2">⚡ INTERVAL (60s)</Badge>
            <h2 className="font-heading text-2xl font-extrabold uppercase tracking-tight">Drinks Break</h2>
          </div>

          <div className="flex items-center justify-around bg-slate-900 rounded-xl py-5 px-4">
            <div className="text-center">
              <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">{sideAName}</p>
              <p className="font-heading text-5xl font-black text-primary tabular-nums">{game.scoreA}</p>
            </div>
            <div className="text-slate-600 text-2xl">–</div>
            <div className="text-center">
              <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">{sideBName}</p>
              <p className="font-heading text-5xl font-black text-secondary tabular-nums">{game.scoreB}</p>
            </div>
          </div>

          {adConfig?.ads_enabled !== false && (
          <div className="rounded-xl border-2 border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-orange-500/10 p-4 space-y-2" data-testid="interval-ad">
            <p className="text-[10px] font-bold uppercase tracking-wider text-amber-500">Premium Interval Ad</p>
            {hasValidImg ? (
              <a href={adClick} target="_blank" rel="noopener noreferrer" className="block">
                <img
                  src={adImg}
                  alt="Ad"
                  className="w-full h-32 object-cover rounded-lg"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </a>
            ) : (
              <a
                href={adClick}
                target="_blank"
                rel="noopener noreferrer"
                className="block h-32 rounded-lg bg-slate-900/60 hover:bg-slate-900/80 transition-colors flex flex-col items-center justify-center text-center px-4"
                data-testid="interval-ad-default-cta"
              >
                <div>
                  <p className="font-heading font-extrabold uppercase tracking-tight text-base text-amber-400">Ultimate Badminton Guide</p>
                  <p className="text-xs text-slate-300 mt-1">To know more about Ultimate badminton guide →</p>
                  {liveCount != null && (
                    <p className="text-[11px] text-orange-300 mt-2 font-medium" data-testid="interval-live-count-ticker">
                      🔥 {liveCount} other matches are live right now
                    </p>
                  )}
                </div>
              </a>
            )}
            {adText && <p className="text-sm text-center font-medium">{adText}</p>}
          </div>
          )}

          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              <Timer className="h-4 w-4 inline mr-1" />
              <span className="font-mono font-bold text-foreground" data-testid="interval-timer">{remaining}s</span> remaining
            </p>
            <Button onClick={onResume} className="h-11 px-6 font-bold" data-testid="interval-resume-btn">
              Resume Play
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
